import { Shield, Map, Package, FlaskConical, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDiscoveryContext } from '../DiscoveryWizard'
import { useCases, type UseCaseId } from '@/lib/discovery-data'

const iconMap = {
  Shield,
  Map,
  Package,
  FlaskConical,
}

export function UseCaseStep() {
  const { state, setUseCase } = useDiscoveryContext()

  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-12 wizard-fade-in">
          <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-3">
            What's your primary use case?
          </h2>
          <p className="text-muted-foreground">
            Select the category that best describes your operations
          </p>
        </div>

        {/* Use case cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {useCases.map((useCase, index) => {
            const Icon = iconMap[useCase.icon as keyof typeof iconMap]
            const isSelected = state.useCase === useCase.id

            return (
              <button
                key={useCase.id}
                onClick={() => setUseCase(useCase.id as UseCaseId)}
                className={cn(
                  'group relative p-6 rounded-lg border-2 text-left transition-all duration-200',
                  'wizard-stagger',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border/30 hover:border-border/60 hover:bg-secondary/20'
                )}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary-foreground" />
                    </div>
                  </div>
                )}

                {/* Icon */}
                <div
                  className={cn(
                    'w-12 h-12 rounded-lg flex items-center justify-center mb-4 transition-colors',
                    isSelected ? 'bg-primary/20' : 'bg-secondary/50 group-hover:bg-secondary'
                  )}
                >
                  <Icon
                    className={cn(
                      'w-6 h-6 transition-colors',
                      isSelected ? 'text-primary' : useCase.color
                    )}
                  />
                </div>

                {/* Content */}
                <h3 className="text-lg font-medium text-foreground mb-1">{useCase.name}</h3>
                <p className="text-sm text-muted-foreground/70">{useCase.tagline}</p>

                {/* Expanded description on selection */}
                {isSelected && (
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border/20 wizard-fade-in">
                    {useCase.description}
                  </p>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

